// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

// CSS imports
import './BlogPosts.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import Alert from '../../components/alert/Alert';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import SearchBar from '../../components/searchBar/SearchBar';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';

// Type imports
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { AlertSize } from '../../types/alert/AlertSize';
import { User } from '../../types/user/User';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BlogPreview } from '../../types/blog/BlogPreview';
import { BlogPostsResult } from '../../types/blog/BlogPostsResult';
import BlogPreviewCard from '../../components/blog/blogPreviewCard/BlogPreviewCard';

type Props = {
  user: User;
};

const BlogPosts: React.FC<Props> = ({ user }: Props) => {
  const [search, setSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogPreview[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getBlogPosts = useCallback(
    async (tempPosts: BlogPreview[], search: string, page: number) => {
      setIsLoading(true);
      const res: BlogPostsResult | null = await ApiService.getBlogPosts(
        search,
        page,
        10
      );
      setIsLoading(false);
      if (res) {
        for (const post of res.blogPosts) {
          tempPosts.push(post);
        }
        setBlogPosts(tempPosts);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    let urlSearch = searchParams.get('search');
    let finalSearch = urlSearch ? decodeURI(urlSearch) : '';

    setSelectedSearch(finalSearch);

    getBlogPosts([], finalSearch, 1);
  }, [searchParams, getBlogPosts]);

  const deleteBlogPost = async (id: string, index: number) => {
    const res: boolean = await ApiService.deleteBlogPost(id);
    if (res) {
      const tempPosts = blogPosts.slice(0, blogPosts.length);
      tempPosts.splice(index, 1);
      setBlogPosts(tempPosts);
    } else {
      setShowUnknownError(true);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const changeSearch = async (newSearch: string) => {
    navigate(
      '/blog' + (newSearch !== '' ? '?search=' + encodeURI(newSearch) : '')
    );
  };

  return (
    <div className='BlogPosts'>
      <Link to='/blog/create' className='add-button'>
        <PrimaryButton ReactIcon={FaPlus} />
      </Link>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h2>Blog</h2>
          <SearchBar
            className='search-bar'
            value={search}
            placeholder='Search'
            onChange={onSearchChange}
            triggerSearch={changeSearch}
          />
          <InfiniteScroll
            dataLength={blogPosts.length}
            hasMore={selectedPage !== pagesCount}
            next={() =>
              getBlogPosts(blogPosts, selectedSearch, selectedPage + 1)
            }
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {/* <FlexSystem> */}
            {blogPosts.map((post: BlogPreview, index: number) => (
              <BlogPreviewCard
                key={index}
                className='item-card'
                user={user}
                index={index}
                post={post}
                deleteBlogPost={deleteBlogPost}
              />
              // <FlexSystemItem key={index} itemSize={6}></FlexSystemItem>
              //   <EmailTemplateCard
              //     className='item-card'
              //     user={user}
              //     index={index}
              //     template={template}
              //     deleteTemplate={deleteTemplate}
              //     key={index}
              //   />
            ))}
            {/* </FlexSystem> */}
          </InfiniteScroll>
          {!isLoading && blogPosts.length === 0 ? (
            <span className='no-items-found'>No blog posts</span>
          ) : null}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default BlogPosts;
